import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withErrorBoundary } from 'react-error-boundary';
import { filter, find, forOwn, get, isEmpty, groupBy } from 'lodash';
import { theme as cprimeTheme } from 'cprime-foundational-components';
import { useCascadingFilterContext } from 'contexts/CascadingFilters.context';
import { useProfileContext } from 'contexts/UserProfile.context';
import { useTableSelectionContext } from 'contexts/TableSelection.context';
import AlertMessage from 'components/AlertMessage/AlertMessage';
import CascadingFilters from 'components/CascadingFilters/CascadingFilters';
import cascadingFiltersSalesAndStockViewConfig from './cascadingFiltersSalesAndStockView.config.json';
import {
  formatDisplayName,
  getLabelForChip,
  hasInvalidMonthRange,
  shiftFEAccessory,
  sortAsc,
} from 'components/CascadingFilters/utils';

const DISTRIBUTOR_CODES = 'PageFilterSalesStock.distributorCodes';

const setTimePeriod = ({
  pageFilterData,
  timePeriod,
  storedFiltersGrouped,
  businessSalesMonthData,
}) => {
  const timePeriodOptions = get(pageFilterData, 'PageFilterSalesStock.timePeriod');
  const storedTimePeriods = storedFiltersGrouped?.timePeriod ?? [];

  timePeriodOptions?.forEach((option) => {
    const displayName = formatDisplayName(option.name);

    const defaultOption = find(storedTimePeriods, {
      field: 'timePeriod',
      value: displayName,
    });

    const isChecked =
      defaultOption?.isChecked !== undefined
        ? defaultOption.isChecked
        : displayName === 'Current Month';

    let isDisabled = false;
    if (displayName === '10-Day') {
      isDisabled = !businessSalesMonthData?.all?.tenDays;
    } else if (displayName === '20-Day') {
      isDisabled = !businessSalesMonthData?.all?.twentyDays;
    }

    const newQuickFilter = {
      displayName,
      value: displayName,
      field: 'timePeriod',
      headerName: 'Time Period',
      isChecked,
      bgColor: cprimeTheme.colors.success[80],
      isDisabled,
    };

    const alreadyIncluded = find(timePeriod, {
      value: displayName,
      field: 'timePeriod',
    });

    if (!alreadyIncluded) {
      timePeriod?.push(newQuickFilter);
    }
  });
};

const setDistributors = ({ distributor, distributorArr, storedFiltersGrouped }) => {
  const { distributorCode } = storedFiltersGrouped;
  distributorArr?.forEach((distr) => {
    let isChecked = true;
    if (distributorCode) {
      const fieldObj = find(distributorCode, {
        field: 'distributorCode',
        value: distr.distributorCode,
      });
      isChecked = !!fieldObj?.isChecked;
    }

    const newDistributor = {
      displayName: formatDisplayName(distr.distributorName),
      value: distr.distributorCode,
      field: 'distributorCode',
      headerName: 'Distributor',
      isChecked,
    };
    const isDistributor = !!find(distributor, newDistributor);
    if (!isDistributor) {
      distributor.push(newDistributor);
    }
  });
};
const setBrands = ({
  distributor,
  brand,
  distributorArr,
  storedFiltersGrouped,
  isCascadingFilterOff,
}) => {
  distributorArr?.forEach((distr) => {
    const isParentDistributorChecked =
      isCascadingFilterOff ||
      !!find(distributor, {
        displayName: formatDisplayName(distr.distributorName),
        value: distr.distributorCode,
        isChecked: true,
      });
    if (isParentDistributorChecked && brand.length < 3) {
      distr.brandCodes.forEach((brandCode) => {
        const { brandName = false } = storedFiltersGrouped;
        let isChecked = true;
        if (brandName) {
          const fieldObj = find(brandName, {
            field: 'brandName',
            value: brandCode.brandCode,
          });
          if (fieldObj) {
            isChecked = fieldObj.isChecked;
          }
        }
        const newBrand = {
          displayName: formatDisplayName(brandCode.brandName),
          value: brandCode.brandCode,
          field: 'brandName',
          headerName: 'Brand',
          isChecked,
        };
        const isBrand = find(brand, newBrand);
        if (!isBrand) {
          brand.push(newBrand);
        }
      });
    }
  });
};
const setCarTruckCodes = ({ pageFilterData, carTruckCode, storedFiltersGrouped }) => {
  const carTruckOptions = get(pageFilterData, 'PageFilterSalesStock.carTruckCodes');
  carTruckOptions?.forEach((indicator) => {
    let isChecked = true;
    const carTruckCodes = storedFiltersGrouped.carTruckCodes;
    if (carTruckCodes) {
      isChecked = find(carTruckCodes, {
        field: 'carTruckCodes',
        value: indicator.value,
      })?.isChecked;
    }
    const newCarTruckCode = {
      displayName: formatDisplayName(indicator.name),
      value: indicator.value,
      field: 'carTruckCodes',
      headerName: 'Car/Truck',
      isChecked,
    };
    const isCarTruckCode = find(carTruckCode, newCarTruckCode);
    if (!isCarTruckCode) {
      carTruckCode.push(newCarTruckCode);
    }
  });
};
const setFleet = ({ pageFilterData, fleetIndicator, storedFiltersGrouped }) => {
  const fleetIndicatorOptions = get(pageFilterData, 'PageFilterSalesStock.fleetIndicators');
  fleetIndicatorOptions?.forEach((indicator) => {
    let isChecked = true;
    const fleetIndicators = storedFiltersGrouped.fleetIndicator;
    if (fleetIndicators) {
      isChecked = find(fleetIndicators, {
        field: 'fleetIndicator',
        value: indicator.value,
      })?.isChecked;
    }
    const newFleetIndicator = {
      displayName: formatDisplayName(indicator.name),
      value: indicator.value,
      field: 'fleetIndicator',
      headerName: 'Fleet',
      isChecked,
    };
    const isFleet = find(fleetIndicator, newFleetIndicator);
    if (!isFleet) {
      fleetIndicator.push(newFleetIndicator);
    }
  });
};
const setRegions = ({
  distributor,
  brand,
  regionCode,
  distributorArr,
  storedFiltersGrouped,
  isCascadingFilterOff,
}) => {
  distributorArr?.forEach((distr) => {
    const isParentDistributorChecked =
      isCascadingFilterOff ||
      !!find(distributor, {
        displayName: formatDisplayName(distr.distributorName),
        value: distr.distributorCode,
        isChecked: true,
      });
    if (isParentDistributorChecked) {
      distr.brandCodes.forEach((brandCode) => {
        const isParentBrandChecked =
          isCascadingFilterOff ||
          !!find(brand, {
            displayName: formatDisplayName(brandCode.brandName),
            value: brandCode.brandCode,
            isChecked: true,
          });

        if (isParentBrandChecked) {
          brandCode.regions.forEach((brandRegion) => {
            const currentRegionCode = storedFiltersGrouped?.currentRegionCode;
            let isChecked = storedFiltersGrouped?.currentRegionCode ? false : true;

            const fieldObj = find(currentRegionCode, {
              field: 'currentRegionCode',
              value: brandRegion.regionCode,
            });
            if (fieldObj) {
              isChecked = fieldObj.isChecked;
            }
            const newRegion = {
              displayName: formatDisplayName(
                `${brandRegion.regionCode} | ${brandRegion.regionName}`,
              ),
              value: brandRegion.regionCode,
              field: 'currentRegionCode',
              headerName: 'Region',
              isChecked,
            };
            const isRegion = !!find(regionCode, newRegion);
            if (!isRegion) {
              regionCode.push(newRegion);
            }
          });
        }
      });
    }
  });
  sortAsc(regionCode);
};
const getDealers = ({
  distributor,
  brand,
  regionCode,
  distributorArr,
  storedFiltersGrouped,
  isCascadingFilterOff,
  countOnly,
}) => {
  const dealerCode = [];
  let showCount = false;
  let dealerCodeCount = 0;
  distributorArr?.forEach((distr) => {
    const isParentDistributorChecked =
      isCascadingFilterOff ||
      !!find(distributor.options, {
        displayName: formatDisplayName(distr.distributorName),
        value: distr.distributorCode,
        isChecked: true,
      });
    if (isParentDistributorChecked) {
      distr.brandCodes.forEach((brandCode) => {
        const isParentBrandChecked =
          isCascadingFilterOff ||
          !!find(brand.options, {
            displayName: formatDisplayName(brandCode.brandName),
            value: brandCode.brandCode,
            isChecked: true,
          });

        if (isParentBrandChecked) {
          brandCode.regions.forEach((brandCodeRegions) => {
            const isParentRegionChecked =
              isCascadingFilterOff ||
              !!find(regionCode.options, {
                value: brandCodeRegions.regionCode,
                isChecked: true,
              });

            if (isParentRegionChecked) {
              brandCodeRegions.dealers.forEach((regionDealer) => {
                const currentDealerCode = storedFiltersGrouped?.currentDealerCode;
                let isChecked = storedFiltersGrouped?.currentDealerCode ? false : true;
                if (currentDealerCode) {
                  const fieldObj = find(currentDealerCode, {
                    field: 'currentDealerCode',
                    value: regionDealer.dealerCode,
                  });
                  if (fieldObj) {
                    isChecked = fieldObj.isChecked;
                  }
                }

                if (countOnly) {
                  if (isChecked) {
                    dealerCodeCount = dealerCodeCount + 1;
                  } else {
                    showCount = true;
                  }
                } else {
                  const newDealer = {
                    displayName: formatDisplayName(
                      `${regionDealer.dealerCode} | ${regionDealer.dealerName}`,
                    ),
                    value: regionDealer.dealerCode,
                    field: 'currentDealerCode',
                    headerName: 'Dealer',
                    isChecked,
                  };
                  const isDealer = find(dealerCode, newDealer);
                  if (!isDealer) {
                    dealerCode.push(newDealer);
                  }
                }
              });
            }
          });
        }
      });
    }
  });

  sortAsc(dealerCode);
  return countOnly ? { showCount, dealerCodeCount } : dealerCode;
};

const setModelYear = ({ pageFilterData, modelYear, storedFiltersGrouped }) => {
  const modelYearsOptions = get(pageFilterData, 'PageFilterSalesStock.modelYears');

  modelYearsOptions?.forEach((year) => {
    let isChecked = true;
    const modelYears = storedFiltersGrouped.modelYear;

    if (modelYears) {
      isChecked = find(modelYears, {
        field: 'modelYear',
        value: year.value,
      })?.isChecked;
    }
    const newModelYear = {
      displayName: year.name,
      value: year.value,
      field: 'modelYear',
      headerName: 'Model Year',
      isChecked,
    };
    const isFleet = find(modelYear, newModelYear);
    if (!isFleet) {
      modelYear.push(newModelYear);
    }
  });
};

const getSeries = ({
  distributor,
  brand,
  distributorArr,
  storedFiltersGrouped,
  isCascadingFilterOff,
  countOnly,
}) => {
  const series = [];
  let showCount = false;
  const uniqueSeries = new Set();

  distributorArr?.forEach((distr) => {
    const isParentDistributorChecked =
      isCascadingFilterOff ||
      !!find(distributor.options, {
        displayName: formatDisplayName(distr.distributorName),
        value: distr.distributorCode,
        isChecked: true,
      });

    if (isParentDistributorChecked) {
      distr.brandCodes.forEach((brandCode) => {
        const isParentBrandChecked =
          isCascadingFilterOff ||
          !!find(brand.options, {
            displayName: formatDisplayName(brandCode.brandName),
            value: brandCode.brandCode,
            isChecked: true,
          });

        if (isParentBrandChecked) {
          brandCode?.salesSeriesNames?.forEach((saleSeriesName) => {
            const salesSeriesName = storedFiltersGrouped?.salesSeriesNames;
            let isChecked = storedFiltersGrouped?.value ? false : true;

            if (salesSeriesName) {
              const fieldObj = find(salesSeriesName, {
                field: 'salesSeriesNames',
                value: saleSeriesName.value,
              });
              if (fieldObj) {
                isChecked = fieldObj.isChecked;
              }
            }

            if (countOnly) {
              if (isChecked) {
                uniqueSeries.add(saleSeriesName.value);
              } else {
                showCount = true;
              }
            } else {
              const newSeries = {
                displayName: saleSeriesName.value,
                value: saleSeriesName.value,
                field: 'salesSeriesNames',
                headerName: 'Series',
                isChecked,
              };

              const isSeries = find(series, newSeries);
              if (!isSeries) {
                series.push(newSeries);
              }
            }
          });
        }
      });
    }
  });

  const seriesCount = uniqueSeries.size;
  sortAsc(series);

  return countOnly ? { showCount, seriesCount } : series;
};

const setDrivetrain = ({ pageFilterData, drivetrain, storedFiltersGrouped }) => {
  const drivetrainOptions = get(pageFilterData, 'PageFilterSalesStock.drivetrainNames');

  drivetrainOptions?.forEach((option) => {
    let isChecked = true;
    const drivetrains = storedFiltersGrouped.drivetrainNames;

    if (drivetrains) {
      isChecked = find(drivetrains, {
        field: 'drivetrainNames',
        value: option.value,
      })?.isChecked;
    }
    const newDrivetrain = {
      displayName: option.value === 'NA' ? 'Not Available' : option.name,
      value: option.value,
      field: 'drivetrainNames',
      headerName: 'Drivetrain',
      isChecked,
    };
    const isDrivetrain = find(drivetrain, newDrivetrain);
    if (!isDrivetrain) {
      drivetrain.push(newDrivetrain);
    }
  });
  sortAsc(drivetrain);

  const naIndex = drivetrain.findIndex((item) => item.value === 'NA');
  if (naIndex > -1) {
    const [naItem] = drivetrain.splice(naIndex, 1);
    drivetrain.push(naItem);
  }
};
const getFIOAccessories = ({
  distributor,
  brand,
  distributorArr,
  storedFiltersGrouped,
  isCascadingFilterOff,
}) => {
  const fioAccessory = [];
  distributorArr?.forEach((distr) => {
    const isParentDistributorChecked =
      isCascadingFilterOff ||
      !!find(distributor.options, {
        displayName: formatDisplayName(distr.distributorName),
        value: distr.distributorCode,
        isChecked: true,
      });
    if (isParentDistributorChecked) {
      distr.brandCodes.forEach((brandCode) => {
        const isParentBrandChecked =
          isCascadingFilterOff ||
          !!find(brand.options, {
            displayName: formatDisplayName(brandCode.brandName),
            value: brandCode.brandCode,
            isChecked: true,
          });

        if (isParentBrandChecked) {
          brandCode.accessoriesFactories.forEach((accessory) => {
            const fioAccessories = storedFiltersGrouped?.accessoriesFactories;
            let isChecked = false;
            if (fioAccessories) {
              const fieldObj = find(fioAccessories, {
                field: 'accessoriesFactories',
                value: accessory.code,
              });
              if (fieldObj) {
                isChecked = fieldObj.isChecked;
              }
            }

            const newAccessory = {
              displayName: `${accessory.code.toUpperCase()} | ${accessory.value}
              `,
              value: accessory.code,
              field: 'accessoriesFactories',
              headerName: 'FIO Accessories',
              isChecked,
            };
            const isAccessory = find(fioAccessory, newAccessory);
            if (!isAccessory) {
              fioAccessory.push(newAccessory);
            }
          });
        }
      });
    }
  });

  sortAsc(fioAccessory);
  shiftFEAccessory(fioAccessory);
  return fioAccessory;
};
const getPPOAccessories = ({
  distributor,
  brand,
  distributorArr,
  storedFiltersGrouped,
  isCascadingFilterOff,
}) => {
  const ppoAccessory = [];
  distributorArr?.forEach((distr) => {
    const isParentDistributorChecked =
      isCascadingFilterOff ||
      !!find(distributor.options, {
        displayName: formatDisplayName(distr.distributorName),
        value: distr.distributorCode,
        isChecked: true,
      });
    if (isParentDistributorChecked) {
      distr.brandCodes.forEach((brandCode) => {
        const isParentBrandChecked =
          isCascadingFilterOff ||
          !!find(brand.options, {
            displayName: formatDisplayName(brandCode.brandName),
            value: brandCode.brandCode,
            isChecked: true,
          });

        if (isParentBrandChecked) {
          brandCode.accessoriesPostProductions.forEach((accessory) => {
            const ppoAccessories = storedFiltersGrouped?.accessoriesPostProductions;
            let isChecked = false;
            if (ppoAccessories) {
              const fieldObj = find(ppoAccessories, {
                field: 'accessoriesPostProductions',
                value: accessory.code,
              });
              if (fieldObj) {
                isChecked = fieldObj.isChecked;
              }
            }

            const newAccessory = {
              displayName: `${accessory.code.toUpperCase()} | ${accessory.value}`,
              value: accessory.code,
              field: 'accessoriesPostProductions',
              headerName: 'PPO Accessories',
              isChecked,
            };
            const isAccessory = find(ppoAccessory, newAccessory);
            if (!isAccessory) {
              ppoAccessory.push(newAccessory);
            }
          });
        }
      });
    }
  });

  const alpha = ppoAccessory.filter((item) => /^[A-Za-z]/.test(item.displayName));
  const numeric = ppoAccessory.filter((item) => /^[0-9]/.test(item.displayName));
  return [...sortAsc(alpha), ...sortAsc(numeric)];
};
const getExteriorColor = ({ pageFilterData, storedFiltersGrouped, countOnly }) => {
  const exteriorColorCode = [];
  let showCount = false;
  let exteriorColorCount = 0;
  const exteriorColorCodes = get(pageFilterData, 'PageFilterSalesStock.exteriorColorDescriptions');

  exteriorColorCodes?.forEach((color) => {
    let isChecked = true;
    const exteriorColor = storedFiltersGrouped.exteriorColorCodes;

    if (exteriorColor) {
      isChecked = find(exteriorColor, {
        field: 'exteriorColorCodes',
        value: color.name,
      })?.isChecked;
    }

    if (countOnly) {
      if (isChecked) {
        exteriorColorCount = exteriorColorCount + 1;
      } else {
        showCount = true;
      }
    } else {
      const newExteriorColor = {
        displayName: `${color.name} | ${color.value}`,
        value: color.name,
        field: 'exteriorColorCodes',
        headerName: 'Exterior Color',
        isChecked,
      };
      const isExteriorColor = find(exteriorColorCode, newExteriorColor);
      if (!isExteriorColor) {
        exteriorColorCode.push(newExteriorColor);
      }
    }
  });
  sortAsc(exteriorColorCode);
  return countOnly ? { showCount, exteriorColorCount } : exteriorColorCode;
};
const getInteriorColor = ({ pageFilterData, storedFiltersGrouped, countOnly }) => {
  const interiorColorCode = [];
  let showCount = false;
  let interiorColorCount = 0;
  const interiorColorCodes = get(pageFilterData, 'PageFilterSalesStock.interiorColorDescriptions');

  interiorColorCodes?.forEach((color) => {
    let isChecked = true;
    const interiorColor = storedFiltersGrouped.interiorColorCodes;

    if (interiorColor) {
      isChecked = find(interiorColor, {
        field: 'interiorColorCodes',
        value: color.name,
      })?.isChecked;
    }

    if (countOnly) {
      if (isChecked) {
        interiorColorCount++;
      } else {
        showCount = true;
      }
    } else {
      const newInteriorColor = {
        displayName: `${color.name} | ${color.value}`,
        value: color.name,
        field: 'interiorColorCodes',
        headerName: 'Interior Color',
        isChecked,
      };
      const isInteriorColor = find(interiorColorCode, newInteriorColor);
      if (!isInteriorColor) {
        interiorColorCode.push(newInteriorColor);
      }
    }
  });
  sortAsc(interiorColorCode);
  return countOnly ? { showCount, interiorColorCount } : interiorColorCode;
};
const getFilterLists = ({
  pageFilterData,
  storedFilters,
  isCascadingFilterOff,
  businessSalesMonthData,
}) => {
  const distributorArr = get(pageFilterData, DISTRIBUTOR_CODES);

  const parameters = {
    timePeriod: [],
    distributor: [],
    brand: [],
    carTruckCode: [],
    fleetIndicator: [],
    regionCode: [],
    dealerCode: [],
    modelYear: [],
    series: [],
    drivetrain: [],
    accessoriesFactories: [],
    accessoriesPostProductions: [],
    exteriorColorCodes: [],
    interiorColorCodes: [],
    distributorArr,
    storedFiltersGrouped: groupBy(storedFilters, 'field'),
    pageFilterData,
    isCascadingFilterOff,
    businessSalesMonthData,
  };

  setTimePeriod(parameters);
  setDistributors(parameters);
  setBrands(parameters);
  setCarTruckCodes(parameters);
  setFleet(parameters);
  setRegions(parameters);
  setModelYear(parameters);
  setDrivetrain(parameters);

  return cascadingFiltersSalesAndStockViewConfig.map(
    ({ field, optionsPath, headerName, type }) => ({
      field,
      options: get(parameters, optionsPath),
      headerName,
      type,
    }),
  );
};

const addAllDistributors = ({ pageFilterData, cascadingFiltersInitial }) => {
  const isDistributorInCascadingFilters = !!find(cascadingFiltersInitial, {
    field: 'distributorCode',
  });
  const distributors = get(pageFilterData, 'PageFilter.distributorCodes');
  const cascadingFiltersInitialClone = [...cascadingFiltersInitial];

  if (!isDistributorInCascadingFilters) {
    if (distributors) {
      distributors.forEach(({ distributorCode }) => {
        cascadingFiltersInitialClone.push({
          field: 'distributorCode',
          value: distributorCode,
          isChecked: true,
        });
      });
    }
  } else {
    cascadingFiltersInitial.forEach((cascadingFilter, index) => {
      if (
        cascadingFilter.field === 'distributorCode' &&
        !find(distributors, { distributorCode: cascadingFilter.distributorCode })
      ) {
        cascadingFiltersInitialClone.splice(index, 1);
      }
    });
  }
  return cascadingFiltersInitialClone;
};

const getCascadingFiltersInitial = ({
  cascadingFiltersLocalRef,
  cascadingFiltersLocal,
  cascadingFilters,
  setIsCascadingFilterOff,
  pageFilterData,
}) => {
  let cascadingFiltersInitial;
  if (!isEmpty(cascadingFiltersLocalRef.current)) {
    cascadingFiltersInitial = cascadingFiltersLocalRef.current.cascadingFiltersLocal;
    cascadingFiltersLocalRef.current.isCascadingFilterOff && setIsCascadingFilterOff(true);
  } else {
    cascadingFiltersInitial = isEmpty(cascadingFiltersLocal)
      ? cascadingFilters
      : cascadingFiltersLocal;
  }
  return addAllDistributors({ pageFilterData, cascadingFiltersInitial });
};

const isFilterSelected = (filters, field) =>
  filters.some((filter) => filter.field === field && filter.isChecked === true);

const CascadingFiltersSalesAndStockView = ({
  cascadingFiltersLocalRef,
  setPage,
  businessSalesMonthData,
}) => {
  const { selectedUserProfile } = useProfileContext();
  const {
    cascadingFilters,
    data: pageFilterData,
    setCascadingFilters,
  } = useCascadingFilterContext();
  const { clearRowSelectionModel } = useTableSelectionContext();

  const [cascadingOptions, setCascadingOptions] = useState([]);
  const [cascadingFiltersLocal, setCascadingFiltersLocal] = useState([]);
  const [isCascadingFilterOff, setIsCascadingFilterOff] = useState(false);

  useEffect(() => {
    if (pageFilterData) {
      const cascadingFiltersInitial = getCascadingFiltersInitial({
        cascadingFiltersLocalRef,
        cascadingFiltersLocal,
        cascadingFilters,
        setIsCascadingFilterOff,
        pageFilterData,
      });

      setInitialCascadingFilters({ cascadingFiltersInitial });
      setCascadingFiltersLocal(cascadingFiltersInitial);
    }
  }, [pageFilterData]);

  useEffect(() => {
    if (pageFilterData) {
      cascadingFiltersLocalRef.current = { cascadingFiltersLocal, isCascadingFilterOff };
      let options = getFilterLists({
        pageFilterData,
        storedFilters: cascadingFiltersLocal,
        isCascadingFilterOff,
        businessSalesMonthData,
      });

      const isFioAccessoriesSelected = isFilterSelected(
        cascadingFiltersLocal,
        'accessoriesFactories',
      );
      const isPpoAccessoriesSelected = isFilterSelected(
        cascadingFiltersLocal,
        'accessoriesPostProductions',
      );
      const isExteriorColorSelected = isFilterSelected(cascadingFiltersLocal, 'exteriorColorCodes');
      const isInteriorColorSelected = isFilterSelected(cascadingFiltersLocal, 'interiorColorCodes');

      let itemsToDisable = [];
      if (isFioAccessoriesSelected) {
        itemsToDisable = ['accessoriesPostProductions', 'exteriorColorCodes', 'interiorColorCodes'];
      } else if (isPpoAccessoriesSelected) {
        itemsToDisable = ['accessoriesFactories', 'exteriorColorCodes', 'interiorColorCodes'];
      } else if (isExteriorColorSelected || isInteriorColorSelected) {
        itemsToDisable = ['accessoriesFactories', 'accessoriesPostProductions'];
      }

      itemsToDisable.forEach((field) => {
        const group = options.find((opt) => opt.field === field);
        if (group) {
          group.isDisabled = true;
        }
      });

      // Disable Exterior/Interior Color filters when selected Month Range is December 2012 or older
      const COLOR_DATA_CUTOFF = '201212';
      const isMonthRangeSelected = cascadingFiltersLocal?.some(
        (f) => f.field === 'timePeriod' && f.value === 'Month Range' && f.isChecked,
      );
      if (isMonthRangeSelected) {
        const businessSalesMonthFilter = cascadingFiltersLocal?.find(
          (f) => f.field === 'businessSalesMonth',
        );
        const monthRangeValues = businessSalesMonthFilter?.value;
        if (monthRangeValues && monthRangeValues.length > 0) {
          const startMonth = monthRangeValues[0];
          if (startMonth && startMonth <= COLOR_DATA_CUTOFF) {
            const colorFieldsToDisable = {
              exteriorColorCodes:
                'Exterior Color data is not available for the selected time period.',
              interiorColorCodes:
                'Interior Color data is not available for the selected time period.',
            };
            Object.entries(colorFieldsToDisable).forEach(([field, tooltip]) => {
              const group = options.find((opt) => opt.field === field);
              if (group) {
                group.isDisabled = true;
                group.disabledTooltip = tooltip;
              }
            });
          }
        }
      }

      setCascadingOptions(options);
    }
  }, [
    cascadingFiltersLocal,
    isCascadingFilterOff,
    selectedUserProfile,
    pageFilterData,
    businessSalesMonthData,
  ]);

  const dealerChipLabel = useMemo(
    () =>
      !isEmpty(cascadingOptions) &&
      getDealers({
        distributor: find(cascadingOptions, { field: 'distributorCode' }),
        brand: find(cascadingOptions, { field: 'brandName' }),
        regionCode: find(cascadingOptions, { field: 'currentRegionCode' }),
        distributorArr: get(pageFilterData, DISTRIBUTOR_CODES),
        storedFiltersGrouped: groupBy(cascadingFiltersLocal, 'field'),
        isCascadingFilterOff,
        countOnly: true,
      }),
    [cascadingOptions, cascadingFiltersLocal, pageFilterData],
  );

  const seriesChipLabel = useMemo(
    () =>
      !isEmpty(cascadingOptions) &&
      getSeries({
        distributor: find(cascadingOptions, { field: 'distributorCode' }),
        brand: find(cascadingOptions, { field: 'brandName' }),
        distributorArr: get(pageFilterData, DISTRIBUTOR_CODES),
        storedFiltersGrouped: groupBy(cascadingFiltersLocal, 'field'),
        isCascadingFilterOff,
        countOnly: true,
      }),
    [cascadingOptions, cascadingFiltersLocal, pageFilterData],
  );

  const exteriorColorChipLabel = useMemo(
    () =>
      !isEmpty(cascadingOptions) &&
      getExteriorColor({
        pageFilterData,
        storedFiltersGrouped: groupBy(cascadingFiltersLocal, 'field'),
        countOnly: true,
      }),
    [cascadingOptions, cascadingFiltersLocal, pageFilterData],
  );

  const interiorColorChipLabel = useMemo(
    () =>
      !isEmpty(cascadingOptions) &&
      getInteriorColor({
        pageFilterData,
        storedFiltersGrouped: groupBy(cascadingFiltersLocal, 'field'),
        countOnly: true,
      }),
    [cascadingOptions, cascadingFiltersLocal, pageFilterData],
  );

  const hasUncheckedSection = () => {
    const allUncheckedSection = cascadingOptions.some((cascadingFilter) => {
      if (!cascadingFilter.options || cascadingFilter.options.length === 0) {
        return false;
      }

      const numOptions = cascadingFilter.options.length;
      let unCheckedOptions = 0;

      cascadingFilter.options.forEach((option) => {
        if (!option.isChecked) {
          unCheckedOptions += 1;
        }
      });
      return numOptions === unCheckedOptions;
    });

    return (
      allUncheckedSection ||
      dealerChipLabel?.dealerCodeCount === 0 ||
      seriesChipLabel?.seriesCount === 0 ||
      exteriorColorChipLabel?.exteriorColorCount === 0 ||
      interiorColorChipLabel?.interiorColorCount === 0 ||
      hasInvalidMonthRange(cascadingFiltersLocal)
    );
  };

  useEffect(() => {
    if (pageFilterData) {
      setCascadingFiltersLocal(cascadingFilters);
    }
  }, [cascadingFilters]);

  const setInitialCascadingFilters = ({ cascadingFiltersInitial }) =>
    setCascadingOptions(
      getFilterLists({
        pageFilterData,
        storedFilters: cascadingFiltersInitial,
        isCascadingFilterOff,
        businessSalesMonthData,
      }),
    );

  const getListOptions = ({ headerName, newCascadingFiltersLocal }) => {
    if (headerName === 'Dealer') {
      return getDealers({
        distributor: find(cascadingOptions, { field: 'distributorCode' }),
        brand: find(cascadingOptions, { field: 'brandName' }),
        regionCode: find(cascadingOptions, { field: 'currentRegionCode' }),
        distributorArr: get(pageFilterData, DISTRIBUTOR_CODES),
        storedFiltersGrouped: groupBy(newCascadingFiltersLocal || cascadingFiltersLocal, 'field'),
        isCascadingFilterOff,
      });
    }
    if (headerName === 'Series') {
      return getSeries({
        distributor: find(cascadingOptions, { field: 'distributorCode' }),
        brand: find(cascadingOptions, { field: 'brandName' }),
        regionCode: find(cascadingOptions, { field: 'currentRegionCode' }),
        distributorArr: get(pageFilterData, DISTRIBUTOR_CODES),
        storedFiltersGrouped: groupBy(newCascadingFiltersLocal || cascadingFiltersLocal, 'field'),
        isCascadingFilterOff,
      });
    }
    if (headerName === 'FIO Accessories') {
      return getFIOAccessories({
        distributor: find(cascadingOptions, { field: 'distributorCode' }),
        brand: find(cascadingOptions, { field: 'brandName' }),
        distributorArr: get(pageFilterData, DISTRIBUTOR_CODES),
        storedFiltersGrouped: groupBy(newCascadingFiltersLocal || cascadingFiltersLocal, 'field'),
        isCascadingFilterOff,
      });
    }
    if (headerName === 'PPO Accessories') {
      return getPPOAccessories({
        distributor: find(cascadingOptions, { field: 'distributorCode' }),
        brand: find(cascadingOptions, { field: 'brandName' }),
        distributorArr: get(pageFilterData, DISTRIBUTOR_CODES),
        storedFiltersGrouped: groupBy(newCascadingFiltersLocal || cascadingFiltersLocal, 'field'),
        isCascadingFilterOff,
      });
    }
    if (headerName === 'Exterior Color') {
      return getExteriorColor({
        pageFilterData,
        storedFiltersGrouped: groupBy(newCascadingFiltersLocal || cascadingFiltersLocal, 'field'),
      });
    }
    if (headerName === 'Interior Color') {
      return getInteriorColor({
        pageFilterData,
        storedFiltersGrouped: groupBy(newCascadingFiltersLocal || cascadingFiltersLocal, 'field'),
      });
    }
    return [];
  };

  const applyFiltersHandler = ({ newCascadingFiltersLocal }) => {
    setPage(0);
    clearRowSelectionModel();
    let filters = [];
    const groupedCascadingFiltersLocal = groupBy(
      newCascadingFiltersLocal || cascadingFiltersLocal,
      'field',
    );

    forOwn(groupedCascadingFiltersLocal, (value, field) => {
      if (field === 'businessSalesMonth') {
        filters = [...filters, ...value];
        return;
      }

      let options;
      if (field === 'currentDealerCode') {
        options = getListOptions({ headerName: 'Dealer', newCascadingFiltersLocal });
      } else if (field === 'salesSeriesNames') {
        options = getListOptions({ headerName: 'Series', newCascadingFiltersLocal });
      } else if (field === 'accessoriesFactories') {
        options = getListOptions({ headerName: 'FIO Accessories', newCascadingFiltersLocal });
      } else if (field === 'accessoriesPostProductions') {
        options = getListOptions({ headerName: 'PPO Accessories', newCascadingFiltersLocal });
      } else if (field === 'exteriorColorCodes') {
        options = getListOptions({ headerName: 'Exterior Color', newCascadingFiltersLocal });
      } else if (field === 'interiorColorCodes') {
        options = getListOptions({ headerName: 'Interior Color', newCascadingFiltersLocal });
      } else {
        options = find(cascadingOptions, { field }).options;
      }

      const valueIsChecked = filter(value, { isChecked: true });

      if (options.length !== valueIsChecked.length) {
        const filterOptions = options.map(({ field, value, isChecked }) => ({
          field,
          value,
          isChecked,
        }));
        filters = [...filters, ...filterOptions];
      }
    });

    setCascadingFilters(filters);
  };

  const getChipLabel = ({ headerName, options }) => {
    if (!options) {
      options = getListOptions({ headerName, newCascadingFiltersLocal: cascadingFiltersLocal });
    }
    const config = cascadingFiltersSalesAndStockViewConfig?.find(
      (config) => config.field === options?.[0]?.field,
    );
    if (headerName === 'Dealer') {
      return dealerChipLabel.showCount ? dealerChipLabel.dealerCodeCount : 'ALL';
    }
    if (headerName === 'Exterior Color') {
      return exteriorColorChipLabel.showCount ? exteriorColorChipLabel.exteriorColorCount : 'ALL';
    }
    if (headerName === 'Interior Color') {
      return interiorColorChipLabel.showCount ? interiorColorChipLabel.interiorColorCount : 'ALL';
    }
    return getLabelForChip({
      headerName,
      options: options,
      isRadio: config?.isRadio,
    });
  };

  return (
    <CascadingFilters
      applyFiltersHandler={applyFiltersHandler}
      businessSalesMonthData={businessSalesMonthData}
      cascadingFiltersConfig={cascadingFiltersSalesAndStockViewConfig}
      cascadingFiltersLocal={cascadingFiltersLocal}
      cascadingFiltersLocalRef={cascadingFiltersLocalRef}
      cascadingOptions={cascadingOptions}
      defaultFilters={pageFilterData?.PageFilterSalesStock?.timePeriod?.map((item) => ({
        field: cascadingFiltersSalesAndStockViewConfig[0].defaultFilter.field,
        value: item.name,
        isChecked: item.value === cascadingFiltersSalesAndStockViewConfig[0].defaultFilter.value,
      }))}
      getCascadingFiltersInitial={getCascadingFiltersInitial}
      getChipLabel={getChipLabel}
      getListOptions={getListOptions}
      hasUncheckedSection={hasUncheckedSection}
      isCascadingFilterOff={isCascadingFilterOff}
      setCascadingFiltersLocal={setCascadingFiltersLocal}
      setCascadingOptions={setCascadingOptions}
      setIsCascadingFilterOff={setIsCascadingFilterOff}
    />
  );
};

CascadingFiltersSalesAndStockView.propTypes = {
  cascadingFiltersLocalRef: PropTypes.object,
  setPage: PropTypes.func,
  businessSalesMonthData: PropTypes.object,
};

export default withErrorBoundary(CascadingFiltersSalesAndStockView, {
  fallback: <AlertMessage />,
});
